// lib
import React, { useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { hasComments } from '@aytm/app/stats/page/components/lib/utils'
import { CUSTOM_QUESTION_REG } from '@aytm/app/stats/page/comments/lib/constants'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

// styles
import style from '@aytm/app/stats/page/question-views/oe/styles/Controls.module.scss'

const itemTemplate = option => (option.hidden ? null : <span dangerouslySetInnerHTML={{ __html: option.text }}></span>)
const valueTemplate = (option, _props) =>
  option ? (
    <span dangerouslySetInnerHTML={{ __html: `Preset: ${option.label || option.text}` }}></span>
  ) : (
    <span dangerouslySetInnerHTML={{ __html: _props.placeholder || 'Select...' }}></span>
  )

const valueExportTemplate = () => <i className="icon-aytmfd-export-v2" />

const WordcloudControls = ({
  model,
  data,
  features,
  collapsed,
  isVerbatimType,
  isSurveyGuest,
  onPresetChange,
  onApplyToAll,
  onExport,
  onToggle
}) => {
  const { id, type, items, preset } = model

  const [status, setStatus] = useState(null)

  const [exporting, setExporting] = useState(false)

  const timer = useRef(null)

  const statusRef = useRef(null)
  statusRef.current = status

  const exportOptions = useMemo(() => {
    const options = [
      { key: 'png', text: 'Download PNG image' },
      { key: 'pdf', text: 'Download PDF document' },
      { key: 'eps', text: 'Download EPS file' }
    ]

    window.Blob != undefined && options.push({ key: 'csv', text: 'Download CSV file' })

    const allowed = ['oe', 'oex', 'cn'].includes(type) || null === items || hasComments(items)
    if (allowed && isVerbatimType && !features.oe_tags_management.disabled) {
      options.push({ key: 'csv-verbatim', text: 'Download all verbatim (CSV)' })
      if (!CUSTOM_QUESTION_REG.test(String(id).split('-')[0])) {
        options.push({ key: 'csv-verbatim-codes', text: 'Download all verbatim with codes (CSV) ' })
      }
    }

    return options
  }, [id, type, items, isVerbatimType, features.oe_tags_management.disabled])

  const onToggleClick = useCallback(() => {
    setStatus('toggling')
    'function' == typeof onToggle && onToggle()

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => 'toggling' == statusRef.current && setStatus(null), 500)

    return () => timer.current && clearTimeout(timer.current)
  }, [onToggle])

  const onExportChange = event => {
    if (['csv-verbatim', 'csv-verbatim-codes'].includes(event.value)) {
      setExporting(true)
    }

    onExport(event.value, () => setExporting(false))
  }

  return (
    <>
      {features.word_clouds.disabled ? null : (
        <>
          {data.controls.presets != undefined ? (
            <div className="p-field p-col-param">
              <Dropdown
                className="p-dropdown-rounded"
                placeholder={data.controls.presets.placeholder}
                value={preset}
                options={data.controls.presets.options}
                optionValue="key"
                optionLabel="text"
                optionDisabled="disabled"
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                fixedDropdownDir="bottom"
                disabled={data.controls.presets.disabled}
                onChange={data.controls.presets.disabled ? null : event => onPresetChange(event.value)}
              />
            </div>
          ) : null}
          <div className="p-field p-col-button">
            <Button
              className="p-button-grey p-button-rounded"
              label={data.controls.applyToAllLabel}
              onClick={onApplyToAll}
            />
          </div>
        </>
      )}
      <div className="p-fluid p-field p-right">
        {features.word_clouds.disabled ? null : (
          <div className="p-field">
            <span className={style.toggle} onClick={onToggleClick}>
              <i
                className={classNames('icon-aytmfd-config', {
                  ['aytmfd-spin']: 'toggling' == status && !collapsed,
                  ['aytmfd-spin-reverse']: 'toggling' == status && collapsed
                })}
              />
            </span>
          </div>
        )}
        {0 < exportOptions.length && !isSurveyGuest ? (
          <div className={`${style.export} p-field`}>
            {exporting ? (
              <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
            ) : (
              <Dropdown
                className="p-dropdown-icon p-dropdown-right"
                placeholder=""
                options={exportOptions}
                optionValue="key"
                optionLabel="text"
                valueTemplate={valueExportTemplate}
                fixedDropdownDir="bottom"
                onChange={onExportChange}
              />
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

WordcloudControls.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  features: PropTypes.object,
  collapsed: PropTypes.bool,
  isVerbatimType: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onPresetChange: PropTypes.func,
  onApplyToAll: PropTypes.func,
  onExport: PropTypes.func,
  onToggle: PropTypes.func
}

WordcloudControls.displayName = 'WordcloudControls'

export default WordcloudControls
